import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCheckCircle } from "react-icons/fi";
import { typography, spacing } from "../styles/designSystem";
import { getScrollVisibility, useScrollProgress } from "../styles/animations";
import { steps } from "../data/process";
import "../styles/process.css";

const Process = () => {
  const sectionRef = useRef(null);
  const progress = useScrollProgress(sectionRef);
  const visibility = getScrollVisibility(progress);

  const handleCardMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const x = mouseX / rect.width - 0.5;
    const y = mouseY / rect.height - 0.5;

    card.style.setProperty("--rx", `${-y * 8}deg`);
    card.style.setProperty("--ry", `${x * 10}deg`);
  };

  const resetCard = (e) => {
    const card = e.currentTarget;

    card.style.setProperty("--rx", "0deg");
    card.style.setProperty("--ry", "0deg");
  };

  return (
    <section
      id="process"
      ref={sectionRef}
      className="relative overflow-hidden bg-primary py-24 text-white md:py-36"
    >
      {/* Background */}
      {/* Animated background */}
      <div
        className="process-background-mesh pointer-events-none absolute inset-0"
        aria-hidden="true"
      />

      {/* Darker green depth at the bottom-right */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_82%_78%,rgba(79,107,53,0.28),transparent_36%)]"
        aria-hidden="true"
      />

      {/* Moving diagonal light beams */}
      <div
        className="process-light-beam pointer-events-none absolute -left-[40%] top-[5%] h-[130%] w-[28%] bg-gradient-to-r from-transparent via-white/20 to-transparent blur-2xl"
        aria-hidden="true"
      />

      <div
        className="process-light-beam pointer-events-none absolute -left-[50%] top-[25%] h-[100%] w-[18%] bg-gradient-to-r from-transparent via-accent/20 to-transparent blur-3xl [animation-delay:4.5s]"
        aria-hidden="true"
      />

      {/* Animated glowing orbs */}
      <div
        className="process-orb pointer-events-none absolute -left-32 top-16 h-96 w-96 rounded-full bg-white/15 blur-[90px]"
        aria-hidden="true"
      />

      <div
        className="process-orb pointer-events-none absolute -right-36 bottom-10 h-[28rem] w-[28rem] rounded-full bg-secondary/30 blur-[110px] [animation-delay:1.4s]"
        aria-hidden="true"
      />

      <div
        className="process-orb pointer-events-none absolute left-[45%] top-[38%] h-64 w-64 rounded-full bg-accent/15 blur-[100px] [animation-delay:3s]"
        aria-hidden="true"
      />

      {/* Subtle grid */}
      <div
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:72px_72px] opacity-40"
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
        }}
        aria-hidden="true"
      />

      {/* Large rotating rings */}
      <div
        className="process-ring pointer-events-none absolute -right-36 top-24 h-80 w-80 rounded-full border border-dashed border-white/15"
        aria-hidden="true"
      >
        <div className="absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/70 shadow-[0_0_20px_rgba(255,255,255,0.5)]" />

        <div className="absolute bottom-[16%] left-[4%] h-2 w-2 rounded-full bg-accent" />
      </div>

      <div
        className="process-ring-reverse pointer-events-none absolute -left-28 bottom-36 h-64 w-64 rounded-full border border-dashed border-secondary/25"
        aria-hidden="true"
      >
        <div className="absolute right-[8%] top-[18%] h-3 w-3 rounded-full bg-secondary/70" />
      </div>

      {/* Flowing wave lines */}
      <svg
        className="pointer-events-none absolute inset-x-0 top-[31%] h-44 w-full text-white/[0.09]"
        viewBox="0 0 1440 180"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
      >
        <path
          className="process-wave-path"
          d="M-40 90 C180 5 350 170 590 80 C810 -5 1020 160 1480 45"
          stroke="currentColor"
          strokeWidth="2"
        />

        <path
          className="process-wave-path"
          d="M-40 125 C210 45 420 195 680 105 C930 20 1160 160 1480 90"
          stroke="currentColor"
          strokeWidth="1"
          style={{ animationDelay: "-3s" }}
        />
      </svg>

      {/* Floating particles */}
      <div
        className="process-particle pointer-events-none absolute left-[8%] top-[27%] h-3 w-3 rounded-full bg-white/60 shadow-[0_0_18px_rgba(255,255,255,0.45)]"
        aria-hidden="true"
      />

      <div
        className="process-particle pointer-events-none absolute right-[14%] top-[46%] h-2 w-2 rounded-full bg-accent shadow-[0_0_18px_rgba(214,179,106,0.55)] [animation-delay:1.2s]"
        aria-hidden="true"
      />

      <div
        className="process-particle pointer-events-none absolute bottom-[22%] left-[18%] h-2.5 w-2.5 rounded-full bg-secondary/80 [animation-delay:2.4s]"
        aria-hidden="true"
      />

      <div
        className="process-particle pointer-events-none absolute bottom-[12%] right-[28%] h-2 w-2 rounded-full bg-white/50 [animation-delay:3.2s]"
        aria-hidden="true"
      />

      {/* Geometric accents */}
      <div
        className="pointer-events-none absolute left-[6%] top-32 h-20 w-20 rotate-12 rounded-[1.75rem] border border-white/15 bg-white/[0.045] backdrop-blur-sm"
        style={{
          transform: `translateY(${(1 - visibility) * -25}px) rotate(${
            12 + visibility * 8
          }deg)`,
        }}
        aria-hidden="true"
      />

      <div
        className="pointer-events-none absolute bottom-32 right-[6%] h-16 w-16 rotate-45 rounded-2xl border border-white/15 bg-secondary/10 backdrop-blur-sm"
        style={{
          transform: `translateY(${(1 - visibility) * 30}px) rotate(${
            45 - visibility * 12
          }deg)`,
        }}
        aria-hidden="true"
      />

      <div
        className="pointer-events-none absolute right-[22%] top-52 h-10 w-10 opacity-25"
        aria-hidden="true"
      >
        <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 rounded-full bg-white" />
        <div className="absolute left-0 top-1/2 h-[2px] w-full -translate-y-1/2 rounded-full bg-white" />
      </div>

      {/* Soft edge shading */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/[0.06] to-transparent"
        aria-hidden="true"
      />

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-secondary/15 to-transparent"
        aria-hidden="true"
      />

      <div className={`relative z-10 ${spacing.container}`}>
        {/* Header */}
        <div
          className="mx-auto mb-16 max-w-4xl text-center md:mb-24"
          style={{
            opacity: visibility,
            transform: `translateY(${(1 - visibility) * 30}px)`,
            transition: "transform 0.05s linear, opacity 0.1s linear",
          }}
        >
          <p className={`${typography.label} text-white/70`}>Process</p>

          <h2 className={`${typography.heading.h2} mt-5 text-white`}>
            A clear process, without unnecessary complexity
          </h2>

          <div className="mt-6 flex justify-center">
            <div
              className="h-1 w-14 rounded-full bg-gradient-to-r from-white to-secondary"
              style={{ transform: `scaleX(${visibility})` }}
            />
          </div>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
            You always know what's happening, what's next, and where your
            project stands.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative mx-auto max-w-6xl">
          <div
            className="process-dashed-line absolute left-1/2 top-4 hidden h-[calc(100%-2rem)] w-[2px] origin-top -translate-x-1/2 rounded-full lg:block"
            style={{ transform: `translateX(-50%) scaleY(${visibility})` }}
          />

          <div
            className="process-dashed-line absolute left-6 top-4 h-[calc(100%-2rem)] w-[2px] origin-top rounded-full lg:hidden"
            style={{ transform: `scaleY(${visibility})` }}
          />

          <div className="space-y-8 lg:space-y-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isLeft = index % 2 === 0;

              const stepVisibility = Math.max(
                0,
                Math.min(1, (visibility - index * 0.13) * 2.1),
              );

              const card = (
                <div
                  className="process-card group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white p-6 text-secondary shadow-2xl shadow-black/20 md:p-7"
                  onMouseMove={handleCardMove}
                  onMouseLeave={resetCard}
                >
                  <div className="relative z-10">
                    <div className="flex items-start justify-between gap-5">
                      <div className="process-icon flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-2xl text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                        <Icon />
                      </div>

                      <p
                        className={`${typography.label} text-secondary/35 text-right group-hover:text-primary`}
                      >
                        {step.label}
                      </p>
                    </div>

                    <h3
                      className={`${typography.card.large} text-3xl mt-7 group-hover:text-primary`}
                    >
                      {step.title}
                    </h3>

                    <p className={`${typography.body.base} mt-4 `}>
                      {step.text}
                    </p>

                    <div className="mt-6 flex flex-wrap gap-2">
                      {step.points.map((point) => (
                        <span
                          key={point}
                          className="process-pill inline-flex items-center gap-2 rounded-full border border-secondary/20 bg-secondary/[0.05] px-3 py-1.5 text-xs font-semibold text-secondary/70 group-hover:border-primary/20 group-hover:bg-primary/10 group-hover:text-primary"
                        >
                          <FiCheckCircle className="text-primary" />
                          {point}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );

              return (
                <div
                  key={step.number}
                  className="relative grid gap-5 pl-16 lg:grid-cols-[1fr,auto,1fr] lg:items-center lg:gap-8 lg:pl-0"
                  style={{
                    opacity: stepVisibility,
                    transform: `translateY(${(1 - stepVisibility) * 28}px)`,
                    transition: "transform 0.05s linear, opacity 0.1s linear",
                  }}
                >
                  <div
                    className={`hidden lg:block ${
                      isLeft ? "order-1" : "order-3"
                    }`}
                    style={{
                      transform: `translateX(${
                        (1 - stepVisibility) * (isLeft ? -45 : 45)
                      }px)`,
                      transition: "transform 0.05s linear",
                    }}
                  >
                    {card}
                  </div>

                  <div className="absolute left-0 top-2 z-20 lg:static lg:order-2">
                    <div
                      className="relative flex h-12 w-12 items-center justify-center rounded-full border border-white/30 bg-white text-sm font-bold text-primary shadow-xl shadow-black/20 md:h-14 md:w-14"
                      style={{
                        transform: `scale(${0.7 + stepVisibility * 0.3}) rotate(${
                          (1 - stepVisibility) * -10
                        }deg)`,
                        transition: "transform 0.05s linear",
                      }}
                    >
                      <span className="absolute inset-0 rounded-full bg-white/40 blur-md" />
                      <span className="relative">{step.number}</span>
                    </div>
                  </div>

                  <div className="lg:hidden">{card}</div>

                  <div
                    className={`hidden lg:block ${
                      isLeft ? "order-3" : "order-1"
                    }`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className="mx-auto mt-16 max-w-3xl rounded-[2rem] border border-white/20 bg-white/[0.16] p-6 text-center text-white shadow-2xl backdrop-blur-md md:p-7"
          style={{
            opacity: Math.max(0.85, visibility),
            transform: `translateY(${(1 - visibility) * 12}px)`,
            transition: "transform 0.05s linear, opacity 0.1s linear",
          }}
        >
          <p className={`${typography.body.large} text-white`}>
            Every project can be adjusted to your situation.
          </p>

          <Link
            to="/contact"
            className="group mt-5 inline-flex items-center gap-3 rounded-full bg-white px-6 py-3 text-sm font-semibold text-primary shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-secondary hover:text-white hover:shadow-xl"
          >
            Start simple
            <FiArrowRight />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Process;
